import { Cloud, Sparkles, Award } from "lucide-react";

/**
 * Infrastructure & Standards bar — appears above the legal footer line on
 * every page, and (in compact form) at the bottom of every certificate.
 *
 * Three monochrome / high-contrast pill badges:
 *   1. Powered by AWS                  (we run on AWS Mumbai · ap-south-1)
 *   2. AI Powered by Google Gemini     (Gemini API generates all paths)
 *   3. Open Badges 2.0 Standard        (every cert ships an OB 2.0 JSON)
 *
 * The text-only design avoids any logo-licensing risk while still being
 * an unambiguous trust-signal. Each badge is keyboard-focusable.
 *
 * Props
 *   compact?: boolean — smaller pills + no border container, used inside
 *     the certificate "Validation Bar".
 *   className?: string — wrapper override hook.
 */
export default function InfraBadges({ compact = false, className = "" }) {
  const sizing = compact
    ? "px-2.5 py-1 text-[9px]"
    : "px-3 py-1.5 text-[10px]";
  const wrapper = compact
    ? `flex flex-wrap items-center justify-center gap-2 ${className}`
    : `flex flex-wrap items-center justify-center gap-2.5 ${className}`;

  return (
    <div className={wrapper} data-testid="infra-badges">
      <Pill testid="badge-aws" sizing={sizing}>
        <Cloud size={compact ? 10 : 11} className="opacity-90" />
        <span className="tracking-[0.18em] font-semibold uppercase">Powered by AWS</span>
      </Pill>
      <Pill testid="badge-gemini" sizing={sizing}>
        <Sparkles size={compact ? 10 : 11} className="opacity-90" />
        <span className="tracking-[0.18em] font-semibold uppercase">
          AI by Google Gemini
        </span>
      </Pill>
      <Pill testid="badge-openbadges" sizing={sizing}>
        <Award size={compact ? 10 : 11} className="opacity-90" />
        <span className="tracking-[0.18em] font-semibold uppercase">
          Open Badges 2.0
        </span>
      </Pill>
    </div>
  );
}

function Pill({ children, testid, sizing }) {
  return (
    <span
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-white/[0.04] text-white/85 ${sizing} font-mono-oak select-none`}
    >
      {children}
    </span>
  );
}

/**
 * Small attribution disclaimer used directly under InfraBadges in the
 * footer. Kept as a separate export so the certificate can render it
 * at 7pt in its own bottom margin without duplicating copy.
 */
export const INFRA_DISCLAIMER =
  "AWS is a trademark of Amazon.com, Inc. or its affiliates. " +
  "Google and Gemini are trademarks of Google LLC. " +
  "Oak AI Campus is a specialized learning platform operated by " +
  "Digiluxcon Technology Services (OPC) Private Limited.";
